import {noop} from '@benchling/js-utils';

import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChatWithDraftMessage} from 'llm/chat-library/ChatActions';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import * as ChatRouterUtil from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {getNoteLinkedFile} from 'notebook/notes/attachments/NoteLinkedFileDAO';
import {NoteLinkedFileTabState} from 'notebook/notes/attachments/NoteLinkedFileTab';
import type {OmniboxActionItem} from 'omnibox/OmniboxAction';
import type {
  ContentResult,
  OmniboxHelpDocsResult,
  OmniboxResponse,
  RecentSearch,
  ResultType,
} from 'omnibox/omniboxTypes';
import {getStore} from 'redux-stores/Store';
import {markAsOpenedSavedSearch} from 'search/saved-searches/SavedSearchesDAO';
import {
  getMetadataText,
  SAVED_SEARCH_SECTION_TO_NAV_SECTION,
  SavedSearch,
  triggerSearchFromInternalLink,
} from 'search/saved-searches/SavedSearchUtil';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import EntityStore from 'stores/EntityStore';

import {
  ANSWER_GROUP,
  ASK_BENCHLING_GROUP,
  CONTENT_MATCHES_GROUP,
  HELP_DOCS_GROUP,
  MAX_ATTACHMENT_ACTIONS,
  MAX_RESULT_ACTIONS,
  MAX_SAVED_SEARCH_ACTIONS,
  RECENT_SEARCH_CHAT_OBJECT_TYPE,
  RECENT_SEARCHES_LIMIT,
  SAVED_SEARCHES_GROUP,
  SEARCH_RESULTS_GROUP,
} from './omniboxConstants';

let attachmentPreviewAbortController: AbortController | null = null;

export function abortAttachmentPreview(): void {
  attachmentPreviewAbortController?.abort();
}

const AI_CHAT_CONTEXT_RESULT_TYPES: ReadonlySet<ResultType> = new Set([
  'search_result',
  'recent_item',
  'content',
]);

/**
 * The item id to attach as chat context for a result's "Start AI chat" link, or undefined for rows
 * that don't resolve to an item. Content matches use their parent entry's entryApiId; others use apiId.
 */
export function getAiChatContextId(action: OmniboxActionItem): string | undefined {
  if (action.resultType == null || !AI_CHAT_CONTEXT_RESULT_TYPES.has(action.resultType)) {
    return undefined;
  }
  return action.apiId ?? action.entryApiId;
}

/**
 * Open a draft AI chat in the sidepanel with the result attached as context, without sending a message.
 * The sidepanel opens at its regular width so the user stays on their current page.
 * originSearchQuery rides on the draft so the created chat can be recorded into recent searches.
 */
export function startAiChatWithResultContext(contextId: string, originSearchQuery?: string): void {
  ChatRouterUtil.openDraftChatInSidepanel(AgentType.ASSISTANT, '', [contextId], originSearchQuery, {
    expanded: false,
  });
}

async function openAskWithQuery(rewrittenQuery: string): Promise<void> {
  const chatType = getChatTypeForNewAgentChat(AgentType.ASSISTANT);
  const {chatSessionId} = await createChatWithDraftMessage(chatType, {
    draft: {
      status: ChatDraftStatus.DRAFT,
      message: {text: rewrittenQuery, selectedFiles: []},
      settings: {
        agentType: AgentType.ASSISTANT,
        publicDataEnabled: false,
      },
    },
  });
  openExistingChat(chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);
}

function openNoteLinkedFileTab(
  entryApiId: string,
  fileId: string,
  fileName: string,
  previewFile: Parameters<typeof NoteLinkedFileTabState.open>[3] | null,
  externalFile: Parameters<typeof NoteLinkedFileTabState.open>[4],
  isPreviewTransformed: boolean
): void {
  const fileState = FileStates.get().get(entryApiId);
  if (fileState?.buffers()) {
    const existingTab = fileState.findTab(
      NoteLinkedFileTabState,
      (tab: NoteLinkedFileTabState) => tab.noteLinkedFileId() === fileId
    );
    if (existingTab != null) {
      existingTab.set({active: true});
      return;
    }
    fileState.buffers().addTab(
      new NoteLinkedFileTabState({
        owningEntityApiId: entryApiId,
        noteLinkedFileId: fileId,
        fileName,
        previewFile,
        externalFile,
        isPreviewTransformed,
      })
    );
  }
}

export async function openAttachmentPreview(
  entryApiId: string,
  noteLinkedFileId: string | null
): Promise<void> {
  if (!noteLinkedFileId) {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  let file;
  try {
    file = await getNoteLinkedFile({uuid: noteLinkedFileId, entryId: entryApiId});
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  if (!file.externalFile) {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  // If the entry is already open, add the tab directly
  const fileState = FileStates.get().get(entryApiId);
  if (fileState?.buffers()) {
    openNoteLinkedFileTab(
      entryApiId,
      file.id,
      file.name,
      file.previewFile,
      file.externalFile,
      file.isPreviewTransformed ?? false
    );
    return;
  }

  // Entry not open — navigate to it first, then add the tab once it loads
  FileBrowserActions.openItem(entryApiId);
  attachmentPreviewAbortController?.abort();
  attachmentPreviewAbortController = new AbortController();
  const signal = attachmentPreviewAbortController.signal;

  const tryOpen = (): boolean => {
    const state = FileStates.get().get(entryApiId);
    if (state?.buffers()) {
      openNoteLinkedFileTab(
        entryApiId,
        file.id,
        file.name,
        file.previewFile,
        file.externalFile!,
        file.isPreviewTransformed ?? false
      );
      return true;
    }
    return false;
  };

  if (tryOpen()) {
    return;
  }

  let cleaned = false;
  const cleanup = (): void => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    FileStates.get().off('add change', listener);
    clearTimeout(timeout);
    signal.removeEventListener('abort', cleanup);
  };

  const listener = (): void => {
    if (signal.aborted) {
      cleanup();
      return;
    }
    if (tryOpen()) {
      cleanup();
    }
  };

  const timeout = setTimeout(cleanup, 5000);
  FileStates.get().on('add change', listener);
  signal.addEventListener('abort', cleanup);
}

export function buildDefaultActions(
  recentSearches: Array<RecentSearch>,
  onRecentSearchTrigger: (query: string) => void,
  // Reopens a chat-backed recent search; when omitted, chat entries re-run their query instead.
  onRecentChatTrigger?: (chatApiId: string) => void
): Array<OmniboxActionItem> {
  const actions: Array<OmniboxActionItem> = [];

  for (const item of recentSearches.slice(0, RECENT_SEARCHES_LIMIT)) {
    const {openedApiId} = item;
    // A chat id is not a file-browser item, so a chat entry with no chat handler re-runs its query.
    let openRecorded: (() => void) | null = null;
    if (openedApiId != null) {
      if (item.openedObjectType !== RECENT_SEARCH_CHAT_OBJECT_TYPE) {
        openRecorded = () => {
          FileBrowserActions.openItem(openedApiId);
        };
      } else if (onRecentChatTrigger != null) {
        openRecorded = () => {
          onRecentChatTrigger(openedApiId);
        };
      }
    }
    actions.push({
      id: `recent-search-${item.queryString}`,
      // Rows that reopen a result or chat are labeled with its name, falling back to the query.
      title: (openRecorded != null ? item.openedName : undefined) ?? item.queryString,
      group: 'Recent Searches',
      resultType: 'recent_search',
      apiId: openRecorded != null ? openedApiId : undefined,
      objectType: openRecorded != null ? item.openedObjectType : undefined,
      // A search that resolved to a specific object or chat links straight back to it and closes the
      // palette; one with nothing to reopen re-runs the query and stays open to show the results.
      closeOnTrigger: openRecorded != null,
      onTrigger:
        openRecorded ??
        (() => {
          onRecentSearchTrigger(item.queryString);
        }),
    });
  }

  return actions;
}

// Name-matches the user's saved searches against the query (like the Saved Searches modal) and caps
// the result to MAX_SAVED_SEARCH_ACTIONS. These render as a pinned section above the streamed search
// results. Selecting one runs the saved search and refreshes its recency.
export function buildSavedSearchActions(
  savedSearches: Array<SavedSearch>,
  query: string
): Array<OmniboxActionItem> {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return [];
  }
  const currentUser = EntityStore.currentUser();
  // The all-searches query returns shared searches the user can see but not necessarily run; the
  // Saved Searches modal and dropdown disable rows the user lacks access to (own searches are always
  // runnable, others only when they have access to the referenced filters and section). Drop those
  // here so the omnibox never offers an unrunnable saved search.
  const isAccessible = (savedSearch: SavedSearch): boolean =>
    (currentUser != null && savedSearch.ownerJson.id === currentUser.id) ||
    savedSearch.hasAccessToFiltersAndSection;
  return savedSearches
    .filter(
      (savedSearch) => isAccessible(savedSearch) && savedSearch.name.toLowerCase().includes(normalizedQuery)
    )
    .slice(0, MAX_SAVED_SEARCH_ACTIONS)
    .map((savedSearch) => ({
      id: `saved-search-${savedSearch.id}`,
      title: savedSearch.name,
      // Mirrors the "Updated <date> by <owner>" metadata shown in the Saved Searches modal.
      description: getMetadataText('', savedSearch.ownerJson.name, savedSearch.modifiedAt, false),
      group: SAVED_SEARCHES_GROUP,
      resultType: 'saved_search',
      onTrigger: () => {
        // Navigate to the saved search's section and run its params.
        triggerSearchFromInternalLink({
          id: savedSearch.id,
          fileId: '',
          searchParams: savedSearch.searchParams,
          section: savedSearch.section,
          name: savedSearch.name,
          description: savedSearch.description,
          ownerName: savedSearch.ownerJson.name,
          modifiedAt: savedSearch.modifiedAt,
        });
        // Mark it as the active saved search for its section (as withSearching._onSelectSavedSearch
        // does) so the listing opens the saved search itself — showing its name/identity — rather than
        // just landing on an anonymous search with the same params.
        getStore().dispatch(
          SearchUIReduxActions.setSelectedSavedSearch(
            savedSearch,
            SAVED_SEARCH_SECTION_TO_NAV_SECTION[savedSearch.section]
          )
        );
        markAsOpenedSavedSearch(savedSearch.id).catch(noop);
      },
    }));
}

export function buildActions(
  // Nullable so attachment matches from the parallel /omnibox/attachments fetch can render before
  // the main search response arrives; a null main response contributes no search/content rows.
  results: OmniboxResponse | null,
  copiedAnswer: boolean,
  onCopyAnswer: () => void,
  helpDocsResults: Map<string, OmniboxHelpDocsResult | 'loading'>,
  attachmentResults: Array<ContentResult> | null = null
): Array<OmniboxActionItem> {
  const actions: Array<OmniboxActionItem> = [];

  if (results?.directAnswer) {
    const answerText = results.directAnswer;
    actions.push({
      id: 'direct-answer',
      title: answerText,
      description: copiedAnswer ? 'Copied to clipboard!' : undefined,
      group: ANSWER_GROUP,
      resultType: 'direct_answer',
      onTrigger: () => {
        navigator.clipboard.writeText(answerText).catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Failed to copy answer to clipboard', e);
        });
        onCopyAnswer();
      },
      closeOnTrigger: false,
    });
  }

  // Search results and entry-content matches share the main list; attachments render in their own
  // "Content matches" section with a separate budget. Sliced to their limits below.
  const searchActions: Array<OmniboxActionItem> = [];
  const entryContentActions: Array<OmniboxActionItem> = [];
  const attachmentActions: Array<OmniboxActionItem> = [];

  const searchResultApiIds = new Set<string>();
  for (const result of results?.searchResults ?? []) {
    searchResultApiIds.add(result.apiId);
    searchActions.push({
      id: `search-${result.apiId}`,
      title: result.name,
      modifiedAt: result.modifiedAt,
      // Kept for CLICKED_RESULT analytics; the section header itself is suppressed in Omnibox.
      group: SEARCH_RESULTS_GROUP,
      objectType: result.objectType,
      resultType: 'search_result',
      apiId: result.apiId,
      onTrigger: () => {
        FileBrowserActions.openItem(result.apiId);
      },
    });
  }

  // Keep one row per entry: extra chunks share a name and click target, so without dedup they'd
  // render as duplicate rows. Results arrive in relevance order, so the first kept is the strongest.
  // Attachments come from the parallel /omnibox/attachments fetch (attachmentResults) below, so any
  // attachment rows in contentResults are ignored here to avoid rendering them twice.
  const seenEntryApiIds = new Set(searchResultApiIds);
  for (const [index, contentResult] of (results?.contentResults ?? []).entries()) {
    if (contentResult.attachmentApiId != null || seenEntryApiIds.has(contentResult.entryApiId)) {
      continue;
    }
    seenEntryApiIds.add(contentResult.entryApiId);
    entryContentActions.push({
      id: `content-${contentResult.entryApiId}-${index}`,
      // Entry content matches render like a normal Entry result.
      title: contentResult.entryName ?? 'Unknown',
      objectType: 'Entry',
      modifiedAt: contentResult.modifiedAt,
      group: SEARCH_RESULTS_GROUP,
      resultType: 'content',
      apiId: contentResult.entryApiId,
      entryApiId: contentResult.entryApiId,
      onTrigger: () => {
        openAttachmentPreview(contentResult.entryApiId, contentResult.noteLinkedFileId);
      },
    });
  }

  // Attachment matches populate their own "Content matches" section from the parallel fetch, so they
  // render whenever that request resolves, independent of the main search. Dedup by file (extra chunks
  // of one attachment share a name and click target).
  const seenAttachmentApiIds = new Set<string>();
  for (const [index, contentResult] of (attachmentResults ?? []).entries()) {
    if (contentResult.attachmentApiId == null || seenAttachmentApiIds.has(contentResult.attachmentApiId)) {
      continue;
    }
    seenAttachmentApiIds.add(contentResult.attachmentApiId);
    attachmentActions.push({
      id: `content-${contentResult.attachmentApiId}-${index}`,
      // Attachments keep their file name, falling back to the parent entry name.
      title: contentResult.attachmentName || `Entry ${contentResult.entryName ?? 'Unknown'}`,
      modifiedAt: contentResult.modifiedAt,
      group: CONTENT_MATCHES_GROUP,
      resultType: 'content',
      entryApiId: contentResult.entryApiId,
      onTrigger: () => {
        openAttachmentPreview(contentResult.entryApiId, contentResult.noteLinkedFileId);
      },
    });
  }

  // Ask/help-docs suggestions render below the search results, so the fast search results stay
  // directly under the input. They come last in the action list so keyboard navigation reaches them
  // after the search results.
  const askSuggestionActions: Array<OmniboxActionItem> = [];
  for (const suggestion of results?.askSuggestions ?? []) {
    if (suggestion.type === 'help_docs') {
      const helpDocsResult = helpDocsResults.get(suggestion.rewrittenQuery);
      const isLoading = helpDocsResult === 'loading';
      const hasResult = helpDocsResult != null && helpDocsResult !== 'loading';
      const hasError = hasResult && helpDocsResult.error != null;
      const hasArticles = hasResult && !hasError && helpDocsResult.articles.length > 0;

      if (isLoading) {
        askSuggestionActions.push({
          id: `help-docs-loading-${suggestion.rewrittenQuery.slice(0, 30)}`,
          title: 'Searching help docs...',
          description: suggestion.reason,
          group: HELP_DOCS_GROUP,
          resultType: 'help_docs_loading',
          onTrigger: noop,
          closeOnTrigger: false,
        });
      } else if (hasArticles) {
        for (const article of helpDocsResult.articles) {
          askSuggestionActions.push({
            id: `help-doc-${article.url.slice(-30)}`,
            title: article.title,
            description: article.paraphrase,
            group: HELP_DOCS_GROUP,
            resultType: 'help_docs_article',
            onTrigger: () => {
              window.open(article.url, '_blank', 'noopener,noreferrer');
            },
          });
        }
      } else {
        // No results, error, or still pending — fall back to Ask Benchling
        askSuggestionActions.push({
          id: `ask-${suggestion.type}-${suggestion.rewrittenQuery.slice(0, 30)}`,
          title: hasError ? 'Help docs search failed' : `Ask: ${suggestion.rewrittenQuery}`,
          description: hasError ? helpDocsResult.error! : suggestion.reason,
          group: ASK_BENCHLING_GROUP,
          resultType: 'ask_suggestion',
          onTrigger: () => {
            openAskWithQuery(suggestion.rewrittenQuery);
          },
        });
      }
    } else {
      askSuggestionActions.push({
        id: `ask-${suggestion.type}-${suggestion.rewrittenQuery.slice(0, 30)}`,
        title: `Ask: ${suggestion.rewrittenQuery}`,
        description: suggestion.reason,
        group: ASK_BENCHLING_GROUP,
        resultType: 'ask_suggestion',
        onTrigger: () => {
          openAskWithQuery(suggestion.rewrittenQuery);
        },
      });
    }
  }
  const primaryActions = [...searchActions, ...entryContentActions];
  actions.push(
    ...primaryActions.slice(0, MAX_RESULT_ACTIONS),
    ...attachmentActions.slice(0, MAX_ATTACHMENT_ACTIONS)
  );

  // Ask/help-docs suggestions come last so they render below the search results and keyboard
  // navigation reaches them after the search results.
  actions.push(...askSuggestionActions);

  return actions;
}
